import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import StickyPageHeader from '../components/common/StickyPageHeader';
import {
  Plus,
  Edit2,
  Trash2,
  Repeat,
  Pause,
  Play,
  Calendar,
  Tag
} from 'lucide-react';
import { useToast } from '../context/ToastContext';
import { useConfig } from '../context/ConfigContext';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import {
  useRecurringTransactionsQuery,
  useDeleteRecurringTransaction,
  useToggleRecurringTransaction,
  useUpdateRecurringTransaction,
  useCategoriesQuery
} from '../hooks/useQueries';
import { getIconComponent } from '../components/common/IconPicker';
import ConfirmModal from '../components/common/ConfirmModal';
import { RecurringTransactionsSkeleton } from '../components/common/Skeleton';

const FREQUENCY_LABELS = {
  weekly: 'Weekly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  yearly: 'Yearly'
};

const FREQUENCY_SHORT = {
  weekly: '/week',
  monthly: '/mo',
  quarterly: '/qtr',
  yearly: '/yr'
};

const FREQUENCY_COLORS = {
  weekly: 'bg-blue-100 text-blue-700',
  monthly: 'bg-purple-100 text-purple-700',
  quarterly: 'bg-amber-100 text-amber-700',
  yearly: 'bg-teal-100 text-teal-700'
};

// Normalize any frequency amount to monthly
function normalizeToMonthly(amount, frequency) {
  switch (frequency) {
    case 'weekly': return amount * (52 / 12);
    case 'monthly': return amount;
    case 'quarterly': return amount / 3;
    case 'yearly': return amount / 12;
    default: return amount;
  }
}

export default function RecurringTransactionsPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { formatAmount, formatDate, config } = useConfig();
  const { hasEncryptionKey } = useAuth();
  const { isModernUI } = useTheme();

  // React Query hooks
  const { data: recurringData = {}, isLoading } = useRecurringTransactionsQuery({}, {
    enabled: hasEncryptionKey
  });
  const deleteRecurringMutation = useDeleteRecurringTransaction();
  const toggleRecurringMutation = useToggleRecurringTransaction();
  const updateRecurringMutation = useUpdateRecurringTransaction();

  const recurringTransactions = recurringData.recurringTransactions || [];

  // Categories for icon lookup
  const { data: categories = [] } = useCategoriesQuery();
  const categoryMap = useMemo(() => {
    const map = {};
    categories.forEach(c => { map[c.name] = c; });
    return map;
  }, [categories]);

  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, transaction: null });
  const [activeTab, setActiveTab] = useState('all');

  // Filter transactions by tab
  const displayedTransactions = useMemo(() => {
    if (activeTab === 'subscriptions') {
      return recurringTransactions.filter(t => t.isSubscription);
    }
    return recurringTransactions;
  }, [recurringTransactions, activeTab]);

  // Subscription summary stats
  const subscriptionSummary = useMemo(() => {
    const subs = recurringTransactions.filter(t => t.isSubscription && t.isActive);
    const monthlyCost = subs.reduce((sum, t) => sum + normalizeToMonthly(t.amount || 0, t.frequency), 0);
    const totalPaid = recurringTransactions
      .filter(t => t.isSubscription)
      .reduce((sum, t) => sum + (t.amount || 0) * (t.executionCount || 0), 0);
    const activeCount = subs.length;
    return { monthlyCost, totalPaid, activeCount };
  }, [recurringTransactions]);

  const handleEdit = (transaction) => {
    navigate(`/recurring/${transaction._id}/edit`);
  };

  const handleDeleteClick = (transaction) => {
    setDeleteConfirm({ open: true, transaction });
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirm.transaction) {
      try {
        await deleteRecurringMutation.mutateAsync(deleteConfirm.transaction._id);
        toast.success('Recurring transaction deleted');
      } catch (error) {
        toast.error('Failed to delete recurring transaction');
      }
    }
    setDeleteConfirm({ open: false, transaction: null });
  };

  const handleToggle = async (transaction) => {
    try {
      await toggleRecurringMutation.mutateAsync(transaction._id);
      toast.success(transaction.isActive ? 'Recurring transaction paused' : 'Recurring transaction resumed');
    } catch (error) {
      toast.error('Failed to update recurring transaction');
    }
  };

  const handleToggleSubscription = async (transaction) => {
    try {
      await updateRecurringMutation.mutateAsync({
        id: transaction._id,
        data: { isSubscription: !transaction.isSubscription }
      });
      toast.success(transaction.isSubscription ? 'Removed subscription tag' : 'Marked as subscription');
    } catch (error) {
      toast.error('Failed to update subscription status');
    }
  };

  const handleAddNew = () => {
    navigate('/recurring/new');
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'income': return config.incomeColor;
      case 'expense': return config.expenseColor;
      case 'transfer': return '#6366f1';
      default: return '#64748b';
    }
  };

  const getTypeIconBg = (type) => {
    switch (type) {
      case 'income': return '#f0fdf4';
      case 'expense': return '#fef2f2';
      case 'transfer': return '#eef2ff';
      default: return '#f8fafc';
    }
  };

  // Summary stats
  const activeCount = recurringTransactions.filter(t => t.isActive).length;
  const pausedCount = recurringTransactions.filter(t => !t.isActive).length;

  // Show skeleton on initial load (no cached data)
  if (isLoading && recurringTransactions.length === 0) {
    return <RecurringTransactionsSkeleton />;
  }

  return (
    <div className="space-y-3 md:space-y-4">
      {/* Mobile Header */}
      <StickyPageHeader className="xl:hidden mb-1">
        <h1 className="text-base font-bold text-surface-900">Recurring</h1>
        <p className="text-[10px] text-surface-500">Scheduled payments & income</p>
      </StickyPageHeader>

      {/* Mobile Segmented Tabs */}
      <div className="xl:hidden grid grid-cols-2 gap-1 p-1 bg-surface-100 rounded-xl">
        <button
          onClick={() => setActiveTab('all')}
          className={`py-2 rounded-lg font-medium text-xs transition-all ${
            activeTab === 'all'
              ? 'bg-white text-surface-900 shadow-sm'
              : 'text-surface-500'
          }`}
        >
          All
        </button>
        <button
          onClick={() => setActiveTab('subscriptions')}
          className={`py-2 rounded-lg font-medium text-xs transition-all ${
            activeTab === 'subscriptions'
              ? 'bg-white text-surface-900 shadow-sm'
              : 'text-surface-500'
          }`}
        >
          Subscriptions
        </button>
      </div>

      {/* Mobile Summary */}
      <div className="xl:hidden">
        {activeTab === 'subscriptions' && subscriptionSummary.activeCount > 0 ? (
          <div className="bg-white rounded-xl border border-surface-100 p-3">
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-surface-50 rounded-lg p-2.5">
                <p className="text-[10px] text-surface-400 mb-0.5">Monthly Cost</p>
                <p className="font-bold text-sm" style={{ color: config.expenseColor }}>
                  {formatAmount(Math.round(subscriptionSummary.monthlyCost))}
                </p>
              </div>
              <div className="bg-surface-50 rounded-lg p-2.5">
                <p className="text-[10px] text-surface-400 mb-0.5">Total Paid</p>
                <p className="font-bold text-sm text-surface-900">
                  {formatAmount(Math.round(subscriptionSummary.totalPaid))}
                </p>
              </div>
              <div className="bg-surface-50 rounded-lg p-2.5">
                <p className="text-[10px] text-surface-400 mb-0.5">Active</p>
                <p className="font-bold text-sm" style={{ color: config.incomeColor }}>
                  {subscriptionSummary.activeCount}
                </p>
              </div>
            </div>
          </div>
        ) : activeTab === 'all' ? (
          <div className="bg-white rounded-xl border border-surface-100 p-3">
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-surface-50 rounded-lg p-2.5">
                <p className="text-[10px] text-surface-400 mb-0.5">Total</p>
                <p className="font-bold text-sm text-surface-900">{recurringTransactions.length}</p>
              </div>
              <div className="bg-surface-50 rounded-lg p-2.5">
                <p className="text-[10px] text-surface-400 mb-0.5">Active</p>
                <p className="font-bold text-sm" style={{ color: config.incomeColor }}>{activeCount}</p>
              </div>
              <div className="bg-surface-50 rounded-lg p-2.5">
                <p className="text-[10px] text-surface-400 mb-0.5">Paused</p>
                <p className="font-bold text-sm text-surface-500">{pausedCount}</p>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {/* Desktop Header */}
      <div className="hidden xl:flex xl:items-center xl:justify-between gap-4">
        <div className="page-header mb-0">
          <h1 className="page-title">Recurring Transactions</h1>
          <p className="page-subtitle">
            Manage your scheduled payments, income, and transfers
          </p>
        </div>
        <button
          onClick={handleAddNew}
          className="inline-flex items-center justify-center px-5 py-3 rounded-xl font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all"
          style={{ backgroundColor: 'var(--accent-bg)', color: 'var(--accent-text)' }}
        >
          <Plus className="w-5 h-5 mr-2" />
          Add Recurring
        </button>
      </div>

      {/* Desktop Summary */}
      <div className="hidden xl:block bg-white rounded-2xl border border-surface-200 p-4">
        <div className="flex flex-wrap items-center gap-4 text-sm">
          <span className="text-surface-600">
            <strong className="text-surface-900">{recurringTransactions.length}</strong> recurring transactions
          </span>
          <span className="text-surface-300">|</span>
          <span style={{ color: config.incomeColor }}>
            <strong>{activeCount}</strong> active
          </span>
          <span className="text-surface-300">|</span>
          <span className="text-surface-500">
            <strong>{pausedCount}</strong> paused
          </span>
        </div>
      </div>

      {/* Desktop Tab Toggle */}
      <div className="hidden xl:flex gap-2">
        <button
          onClick={() => setActiveTab('all')}
          className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all ${
            activeTab === 'all'
              ? 'text-white'
              : 'bg-surface-100 text-surface-600'
          }`}
          style={activeTab === 'all' ? { backgroundColor: 'var(--accent-bg)' } : {}}
        >
          All
        </button>
        <button
          onClick={() => setActiveTab('subscriptions')}
          className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all flex items-center gap-1.5 ${
            activeTab === 'subscriptions'
              ? 'text-white'
              : 'bg-surface-100 text-surface-600'
          }`}
          style={activeTab === 'subscriptions' ? { backgroundColor: 'var(--accent-bg)' } : {}}
        >
          <Tag className="w-3 h-3" />
          Subscriptions
        </button>
      </div>

      {/* Desktop Subscriptions Summary */}
      {activeTab === 'subscriptions' && subscriptionSummary.activeCount > 0 && (
        <div className="hidden xl:block bg-white rounded-2xl border border-surface-200 p-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-surface-50 rounded-lg p-3">
              <p className="text-xs text-surface-500 mb-0.5">Monthly Cost</p>
              <p className="font-semibold text-base" style={{ color: config.expenseColor }}>
                {formatAmount(Math.round(subscriptionSummary.monthlyCost))}
              </p>
            </div>
            <div className="bg-surface-50 rounded-lg p-3">
              <p className="text-xs text-surface-500 mb-0.5">Total Paid</p>
              <p className="font-semibold text-surface-900 text-base">
                {formatAmount(Math.round(subscriptionSummary.totalPaid))}
              </p>
            </div>
            <div className="bg-surface-50 rounded-lg p-3">
              <p className="text-xs text-surface-500 mb-0.5">Active</p>
              <p className="font-semibold text-base" style={{ color: config.incomeColor }}>
                {subscriptionSummary.activeCount}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && displayedTransactions.length === 0 ? (
        <div className="card text-center py-12">
          <div className="w-16 h-16 bg-surface-100 rounded-full flex items-center justify-center mx-auto mb-4">
            {activeTab === 'subscriptions' ? (
              <Tag className="w-8 h-8 text-surface-400" />
            ) : (
              <Repeat className="w-8 h-8 text-surface-400" />
            )}
          </div>
          <h3 className="text-lg font-medium text-surface-900 mb-2">
            {activeTab === 'subscriptions' ? 'No subscriptions tracked' : 'No recurring transactions'}
          </h3>
          <p className="text-surface-500 mb-4">
            {activeTab === 'subscriptions'
              ? 'Mark recurring expenses as subscriptions to track costs and get renewal reminders.'
              : 'Schedule your regular payments and income to keep things on track'}
          </p>
          {activeTab !== 'subscriptions' && (
            <button
              onClick={handleAddNew}
              className="btn-primary inline-flex"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Recurring Transaction
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="hidden xl:block card overflow-hidden p-0">
            <table className="w-full">
              <thead>
                <tr className="table-header border-b border-surface-200">
                  <th className="table-cell">Description</th>
                  <th className="table-cell w-28">Type</th>
                  <th className="table-cell w-28">Frequency</th>
                  <th className="table-cell w-36">Next Run</th>
                  <th className="table-cell w-32 text-right">Amount</th>
                  <th className="table-cell w-28">Status</th>
                  <th className="table-cell text-right w-32">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-100">
                {displayedTransactions.map((transaction) => {
                  const typeColor = getTypeColor(transaction.type);
                  const catName = transaction.category?.name || transaction.category;
                  const cat = categoryMap[catName];
                  const CatIcon = cat ? getIconComponent(cat.icon) : null;
                  return (
                    <tr
                      key={transaction._id}
                      className={`hover:bg-surface-50 transition-colors ${!transaction.isActive ? 'opacity-60' : ''}`}
                    >
                      <td className="table-cell">
                        <div className="flex items-center gap-3">
                          <div
                            className="w-10 h-10 rounded-xl flex items-center justify-center text-lg"
                            style={{ backgroundColor: getTypeIconBg(transaction.type) }}
                          >
                            {CatIcon ? <CatIcon className="w-5 h-5" /> : '🔁'}
                          </div>
                          <div>
                            <div className="flex items-center gap-1.5">
                              <span className="font-medium text-surface-900">
                                {transaction.description || 'Transfer'}
                              </span>
                              {transaction.isSubscription && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-100 text-violet-700">
                                  Sub
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-surface-500">
                              {transaction.type === 'transfer'
                                ? `${transaction.account?.name || transaction.bankName} → ${transaction.toAccount?.name || transaction.toAccount}`
                                : `${catName} · ${transaction.account?.name || transaction.bankName}`}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="table-cell">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          transaction.type === 'income'
                            ? 'bg-income-100 text-income-700'
                            : transaction.type === 'expense'
                            ? 'bg-expense-100 text-expense-700'
                            : 'bg-primary-100 text-primary-700'
                        }`}>
                          {transaction.type}
                        </span>
                      </td>
                      <td className="table-cell">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${FREQUENCY_COLORS[transaction.frequency]}`}>
                          {FREQUENCY_LABELS[transaction.frequency]}
                        </span>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center gap-1.5 text-sm text-surface-600">
                          <Calendar className="w-3.5 h-3.5" />
                          {transaction.nextRunDate ? formatDate(transaction.nextRunDate) : '-'}
                        </div>
                      </td>
                      <td className="table-cell text-right">
                        <span className="text-sm font-semibold" style={{ color: typeColor }}>
                          {formatAmount(transaction.amount || 0)}
                        </span>
                      </td>
                      <td className="table-cell">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          transaction.isActive
                            ? 'bg-income-100 text-income-700'
                            : 'bg-surface-100 text-surface-600'
                        }`}>
                          {transaction.isActive ? 'Active' : 'Paused'}
                        </span>
                      </td>
                      <td className="table-cell text-right">
                        <div className="flex items-center justify-end gap-1">
                          {transaction.type === 'expense' && (
                            <button
                              onClick={() => handleToggleSubscription(transaction)}
                              className={`p-2 rounded-lg transition-colors ${
                                transaction.isSubscription
                                  ? 'text-violet-600 bg-violet-50 hover:bg-violet-100'
                                  : 'text-surface-400 hover:text-violet-500 hover:bg-violet-50'
                              }`}
                              title={transaction.isSubscription ? 'Remove subscription tag' : 'Mark as subscription'}
                            >
                              <Tag className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleToggle(transaction)}
                            className={`p-2 rounded-lg transition-colors ${
                              transaction.isActive
                                ? 'text-amber-500 hover:text-amber-600 hover:bg-amber-50'
                                : 'text-income-500 hover:text-income-600 hover:bg-income-50'
                            }`}
                            title={transaction.isActive ? 'Pause' : 'Resume'}
                          >
                            {transaction.isActive ? (
                              <Pause className="w-4 h-4" />
                            ) : (
                              <Play className="w-4 h-4" />
                            )}
                          </button>
                          <button
                            onClick={() => handleEdit(transaction)}
                            className="p-2 text-primary-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteClick(transaction)}
                            className="p-2 text-expense-500 hover:text-expense-600 hover:bg-expense-50 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards — Grouped in single white card */}
          <div className="xl:hidden pb-20">
            <div className="bg-white rounded-xl border border-surface-100 overflow-hidden">
              {displayedTransactions.map((transaction, idx) => {
                const typeColor = getTypeColor(transaction.type);
                const catName = transaction.category?.name || transaction.category;
                const cat = categoryMap[catName];
                const CatIcon = cat ? getIconComponent(cat.icon) : null;
                return (
                  <div
                    key={transaction._id}
                    className={`p-3 ${!transaction.isActive ? 'opacity-50' : ''} ${idx > 0 ? 'border-t border-surface-100' : ''}`}
                  >
                    {/* Top row: icon + info + amount */}
                    <div className="flex items-start gap-2.5 mb-1.5">
                      <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 text-lg"
                        style={{ backgroundColor: getTypeIconBg(transaction.type) }}
                      >
                        {CatIcon ? <CatIcon className="w-5 h-5" /> : '🔁'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <p className="font-semibold text-[14px] text-surface-900 truncate">
                            {transaction.description || 'Transfer'}
                          </p>
                          {transaction.isSubscription && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold bg-violet-100 text-violet-700 flex-shrink-0">
                              Sub
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-[11px] text-surface-400">
                            {transaction.type === 'transfer'
                              ? `Transfer · ${transaction.account?.name || transaction.bankName} → ${transaction.toAccount?.name || transaction.toAccount}`
                              : `${transaction.type.charAt(0).toUpperCase() + transaction.type.slice(1)} · ${FREQUENCY_LABELS[transaction.frequency]}`}
                          </span>
                          {catName && transaction.type !== 'transfer' && (
                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium ${FREQUENCY_COLORS[transaction.frequency] || 'bg-surface-100 text-surface-600'}`}>
                              {catName}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0 pl-1">
                        <p className="font-extrabold text-[15px] whitespace-nowrap" style={{ color: typeColor }}>
                          {formatAmount(transaction.amount || 0)}
                        </p>
                        <p className="text-[10px] text-surface-400 text-right">{FREQUENCY_SHORT[transaction.frequency]}</p>
                      </div>
                    </div>
                    {/* Bottom row: next date + actions */}
                    <div className="flex items-center justify-between pl-[50px]">
                      <div className="flex items-center gap-1 text-[11px] text-surface-400">
                        <Calendar className="w-3 h-3" />
                        <span>Next: {transaction.nextRunDate ? formatDate(transaction.nextRunDate) : '-'}</span>
                      </div>
                      <div className="flex items-center gap-0.5">
                        {transaction.type === 'expense' && (
                          <button
                            onClick={() => handleToggleSubscription(transaction)}
                            className={`p-1.5 rounded-lg transition-colors ${
                              transaction.isSubscription ? 'text-violet-600 bg-violet-50' : 'text-surface-300'
                            }`}
                          >
                            <Tag className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button
                          onClick={() => handleToggle(transaction)}
                          className={`p-1.5 rounded-lg transition-colors ${
                            transaction.isActive
                              ? 'text-amber-500 hover:bg-amber-50'
                              : 'text-income-500 hover:bg-income-50'
                          }`}
                        >
                          {transaction.isActive ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                        </button>
                        <button
                          onClick={() => handleEdit(transaction)}
                          className="p-1.5 text-primary-500 hover:bg-primary-50 rounded-lg transition-colors"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(transaction)}
                          className="p-1.5 text-expense-500 hover:bg-expense-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* FAB for Mobile */}
      <button
        onClick={handleAddNew}
        className="fab"
        style={{ opacity: 0.85 }}
        aria-label="Add recurring transaction"
      >
        <Plus className="w-6 h-6" />
      </button>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, transaction: null })}
        onConfirm={handleDeleteConfirm}
        title="Delete Recurring Transaction"
        message="Are you sure you want to delete this recurring transaction? This will not affect any transactions already created."
        confirmText="Delete"
        variant="danger"
      />
    </div>
  );
}
